import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logisticsApi } from '../../api/logistics';

// 模拟物流服务数据
const MOCK_LOGISTICS_SERVICE_DATA = [
  {
    id: 1,
    name: '顺丰速运',
    code: 'SF',
    description: '全国知名快递品牌，服务范围广',
    status: 'ACTIVE',
    supportedRegions: ['全国'],
    createTime: '2023-06-15 10:30:00',
    updateTime: '2023-06-15 10:30:00'
  },
  {
    id: 2,
    name: '中通快递',
    code: 'ZTO',
    description: '性价比高，覆盖面广',
    status: 'INACTIVE',
    supportedRegions: ['华东', '华北'],
    createTime: '2023-05-20 14:45:00',
    updateTime: '2023-06-20 09:15:00'
  },
  {
    id: 3,
    name: '圆通速递',
    code: 'YTO',
    description: '电商快递服务领先品牌',
    status: 'ACTIVE',
    supportedRegions: ['全国'],
    createTime: '2023-07-01 09:15:00',
    updateTime: '2023-07-01 09:15:00'
  }
];

// 模拟售后服务数据
const MOCK_AFTER_SALES_SERVICE_DATA = [
  {
    id: 1,
    type: '退货服务',
    description: '7天无理由退货',
    processingTime: '3-5个工作日',
    status: 'ACTIVE',
    createTime: '2023-06-15 10:30:00',
    updateTime: '2023-06-15 10:30:00'
  },
  {
    id: 2,
    type: '换货服务',
    description: '商品有质量问题可申请换货',
    processingTime: '5-7个工作日',
    status: 'INACTIVE',
    createTime: '2023-05-20 14:45:00',
    updateTime: '2023-06-20 09:15:00'
  }
];

// 模拟运费服务数据
const MOCK_FREIGHT_SERVICE_DATA = [
  {
    id: 1,
    name: '标准快递',
    basePrice: 10,
    freeShippingThreshold: 200,
    description: '全国标准快递服务',
    status: 'ACTIVE',
    createTime: '2023-06-15 10:30:00',
    updateTime: '2023-06-15 10:30:00'
  },
  {
    id: 2,
    name: '次日达',
    basePrice: 20,
    freeShippingThreshold: 500,
    description: '24小时内送达',
    status: 'ACTIVE',
    createTime: '2023-05-20 14:45:00',
    updateTime: '2023-06-20 09:15:00'
  }
];

// 获取物流服务列表
export const fetchLogisticsServiceList = createAsyncThunk(
  'logistics/fetchServiceList',
  async ({ page = 1, pageSize = 10, ...params }, { rejectWithValue }) => {
    try {
      const response = await logisticsApi.getLogisticsServiceList({ page, pageSize, ...params });
      return response.data;
    } catch (error) {
      console.warn('获取物流服务列表失败，使用模拟数据', error);
      
      // 模拟搜索功能
      const filteredData = MOCK_LOGISTICS_SERVICE_DATA.filter(service => {
        const nameMatch = !params.name || 
          service.name.toLowerCase().includes(params.name.toLowerCase());
        
        const codeMatch = !params.code || 
          service.code.toLowerCase().includes(params.code.toLowerCase());
        
        return nameMatch && codeMatch;
      });

      // 模拟分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return {
        list: paginatedData,
        total: filteredData.length,
        page,
        pageSize
      };
    }
  }
);

// 创建物流服务
export const createLogisticsService = createAsyncThunk(
  'logistics/createService',
  async (serviceData, { rejectWithValue }) => {
    try {
      const response = await logisticsApi.createLogisticsService(serviceData);
      return response.data;
    } catch (error) {
      console.warn('创建物流服务失败，使用模拟数据', error);
      const newService = {
        id: MOCK_LOGISTICS_SERVICE_DATA.length + 1,
        ...serviceData,
        status: 'ACTIVE',
        createTime: new Date().toLocaleString(),
        updateTime: new Date().toLocaleString()
      };
      MOCK_LOGISTICS_SERVICE_DATA.push(newService);
      return newService;
    }
  }
);

// 更新物流服务
export const updateLogisticsService = createAsyncThunk(
  'logistics/updateService',
  async ({ id, ...serviceData }, { rejectWithValue }) => {
    try {
      const response = await logisticsApi.updateLogisticsService(id, serviceData);
      return response.data;
    } catch (error) {
      console.warn('更新物流服务失败，使用模拟数据', error);
      const index = MOCK_LOGISTICS_SERVICE_DATA.findIndex(s => s.id === id);
      if (index !== -1) {
        const updatedService = { 
          ...MOCK_LOGISTICS_SERVICE_DATA[index], 
          ...serviceData,
          updateTime: new Date().toLocaleString()
        };
        MOCK_LOGISTICS_SERVICE_DATA[index] = updatedService;
        return updatedService;
      }
      return rejectWithValue('物流服务不存在');
    }
  }
);

// 删除物流服务
export const deleteLogisticsService = createAsyncThunk(
  'logistics/deleteService',
  async (id, { rejectWithValue }) => {
    try {
      await logisticsApi.deleteLogisticsService(id);
      return id;
    } catch (error) {
      console.warn('删除物流服务失败，使用模拟数据', error);
      const index = MOCK_LOGISTICS_SERVICE_DATA.findIndex(s => s.id === id);
      if (index !== -1) {
        MOCK_LOGISTICS_SERVICE_DATA.splice(index, 1);
        return id;
      }
      return rejectWithValue('物流服务不存在');
    }
  }
);

// 获取售后服务列表
export const fetchAfterSalesServiceList = createAsyncThunk(
  'logistics/fetchAfterSalesList',
  async ({ page = 1, pageSize = 10, ...params }, { rejectWithValue }) => {
    try {
      const response = await logisticsApi.getAfterSalesServiceList({ page, pageSize, ...params });
      return response.data;
    } catch (error) {
      console.warn('获取售后服务列表失败，使用模拟数据', error);
      
      // 模拟搜索功能
      const filteredData = MOCK_AFTER_SALES_SERVICE_DATA.filter(service => {
        const typeMatch = !params.type || 
          service.type === params.type;
        
        return typeMatch;
      });

      // 模拟分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return {
        list: paginatedData,
        total: filteredData.length,
        page,
        pageSize
      };
    }
  }
);

// 获取运费服务列表
export const fetchFreightServiceList = createAsyncThunk(
  'logistics/fetchFreightList',
  async ({ page = 1, pageSize = 10, ...params }, { rejectWithValue }) => {
    try {
      const response = await logisticsApi.getFreightServiceList({ page, pageSize, ...params });
      return response.data;
    } catch (error) {
      console.warn('获取运费服务列表失败，使用模拟数据', error);
      
      // 模拟搜索功能
      const filteredData = MOCK_FREIGHT_SERVICE_DATA.filter(service => {
        const nameMatch = !params.name || 
          service.name.toLowerCase().includes(params.name.toLowerCase());
        
        return nameMatch;
      });

      // 模拟分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return {
        list: paginatedData,
        total: filteredData.length,
        page,
        pageSize
      };
    }
  }
);

const logisticsSlice = createSlice({
  name: 'logistics',
  initialState: {
    logisticsServiceList: [],
    afterSalesServiceList: [],
    freightServiceList: [],
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0
    },
    loading: false,
    error: null
  },
  reducers: {
    setLogisticsPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // 物流服务列表
      .addCase(fetchLogisticsServiceList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLogisticsServiceList.fulfilled, (state, action) => {
        state.loading = false;
        state.logisticsServiceList = action.payload.list;
        state.pagination = {
          current: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total
        };
      })
      .addCase(fetchLogisticsServiceList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.logisticsServiceList = MOCK_LOGISTICS_SERVICE_DATA;
        state.pagination.total = MOCK_LOGISTICS_SERVICE_DATA.length;
      })
      // 创建物流服务
      .addCase(createLogisticsService.fulfilled, (state, action) => {
        state.logisticsServiceList.push(action.payload);
      })
      // 更新物流服务
      .addCase(updateLogisticsService.fulfilled, (state, action) => {
        const index = state.logisticsServiceList.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.logisticsServiceList[index] = action.payload;
        }
      })
      // 删除物流服务
      .addCase(deleteLogisticsService.fulfilled, (state, action) => {
        state.logisticsServiceList = state.logisticsServiceList.filter(s => s.id !== action.payload);
      })
      // 售后服务列表
      .addCase(fetchAfterSalesServiceList.fulfilled, (state, action) => {
        state.loading = false;
        state.afterSalesServiceList = action.payload.list;
        state.pagination = {
          current: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total
        };
      })
      // 运费服务列表
      .addCase(fetchFreightServiceList.fulfilled, (state, action) => {
        state.loading = false;
        state.freightServiceList = action.payload.list;
        state.pagination = {
          current: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total
        };
      });
  }
});

export const { setLogisticsPagination } = logisticsSlice.actions;
export default logisticsSlice.reducer; 